/**
 * Per-control detail page — V1035-2-2 / ADR-0071, PD-2; V109-1-19 (B4, H2, H9; round 3 R2-H2..H9).
 *
 * Route: /w/:slug/app/programs/:program/controls/:requirementId
 * Shows one control: its statement (with the program's parameter values), its objectives as the
 * objective engine judges them in the program's newest period, and the action each one needs.
 *
 * Reads: GET /programs/:program/controls/:requirementId → { control } (any member; cross-tenant 404).
 *   `control.readiness` (V109-1-19 B4) carries each objective's status and, when GAC Pro couldn't
 *   check its evidence, the reasons with their sources and unverified resources — so an objective
 *   that couldn't be checked reads as Couldn't check with its fix, never a Gap with "Collect evidence".
 *   R3 (R2-H5): `readiness.risk_acceptances` — an open acceptance is shown, never a clear.
 * Writes: POST /programs/:program/controls/:requirementId/revisions (owner / admin / program lead /
 *   the control's owner; the route decides) from the inline statement editor (H9: a value your
 *   organization must choose is set here, not in Setup; `?set=param` opens it on the value, R2-H2);
 *   POST …/periods/:periodId/evidence (UploadForObjective) for what GAC Pro can't check (R2-H3);
 *   POST /risk-acceptances (owner / admin) once for the page.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, Spinner, Textarea } from '@aigovops/design-system';
import { sourceNameOf } from '@aigovops/core';
import { ApiError, listWorkspaceMembers, req, type RiskAcceptanceSummary } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { useOptionalAuth } from '../lib/auth';
import { programTitle } from '../lib/nav-config';
import { usePrograms } from '../lib/workspace-navigation';
import { currentStage } from '../lib/sweep-model';
import { coveringAcceptance, distinctFixes, nextCollectionText, objectiveItems, type FixContext, type FixLink, type ObjectiveCouldnt } from '../lib/couldnt-check';
import { Chip } from '../components/today/shared';
import { AcceptedLine, AcceptRisk, CouldntCheckChip, CouldntCheckWhy, ShowAllReasons } from '../components/CouldntCheck';
import { UploadForObjective } from '../components/programs/CmmcAssess';
import './programs.css';

/** One objective as the control route returns it (objective readiness, V109-1-19 B4). */
export interface ControlObjective {
  objective_id: string;
  ref: string;
  statement: string;
  status: 'met' | 'not_met' | 'no_evidence' | 'undetermined' | 'not_applicable';
  couldnt_check?: ObjectiveCouldnt;
}

/** GET /programs/:program/controls/:requirementId — the real shape (`{ control }`). */
export interface ProgramControlResponse {
  control: {
    requirement_id: string;
    ref: string;
    title: string;
    program: string;
    implementation_status: string | null;
    requirement: { statement: string | null; params: Array<{ id: string; label?: string | null }> | null };
    current_revision: { revision_id: string; statement: string; org_param_values: Record<string, string | string[]> } | null;
    assignments: Array<{ user_id: string; responsibility: string }>;
    readiness: {
      period_id: string;
      status: 'met' | 'not_met' | 'no_evidence' | 'undetermined';
      couldnt_check: boolean;
      objectives: ControlObjective[];
      risk_acceptances?: RiskAcceptanceSummary[];
    } | null;
    /** V109-7-3 (PD-1): each parameter's source and the rules that read it; `odp_unset` = values not chosen yet. */
    parameters?: {
      parameters: Array<{ id: string; label: string; source: 'baseline' | 'organization' | 'unset'; value: string | string[] | null; read_by: string[] }>;
      odp_unset: string[];
      odp_unset_read_by_rules: string[];
    };
  };
}

const OBJ_CHIP: Record<ControlObjective['status'], { text: string; tone: 'ok' | 'amber' | 'red' | 'neutral' }> = {
  met: { text: 'Met', tone: 'ok' },
  not_met: { text: 'Not met', tone: 'red' },
  no_evidence: { text: 'No evidence', tone: 'red' },
  undetermined: { text: 'Not judged yet', tone: 'neutral' },
  not_applicable: { text: 'N/A', tone: 'neutral' },
};

const anchor = (ref: string) => `objective-${ref.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
const isGap = (o: ControlObjective) => !o.couldnt_check && (o.status === 'not_met' || o.status === 'no_evidence' || o.status === 'undetermined');
const valueText = (v: string | string[] | undefined) => (Array.isArray(v) ? v.join(', ') : v ?? '');

/** What the statement editor was opened for (focus returns to `opener` when it closes, R2-H8). */
interface EditorState { focusParam: boolean; opener: HTMLElement | null; objectiveId: string | null }

export function ControlDetail(): React.ReactElement {
  const { slug, program, requirementId } = useParams<{ slug: string; program: string; requirementId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { role } = useWorkspace();
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const qc = useQueryClient();
  const [statementOpen, setStatementOpen] = useState(false);
  const [editing, setEditing] = useState<EditorState | null>(null);
  const [saved, setSaved] = useState<{ objectiveId: string | null; text: string } | null>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const savedRef = useRef<HTMLParagraphElement>(null);
  const footer = useRef<HTMLDivElement>(null);
  const prefix = `/w/${slug}/app`;
  const admin = role === 'owner' || role === 'admin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['control-detail', slug, program, requirementId],
    queryFn: () => req<ProgramControlResponse>(`/api/w/${slug}/programs/${encodeURIComponent(program!)}/controls/${encodeURIComponent(requirementId!)}`),
    enabled: !!slug && !!program && !!requirementId,
  });
  const members = useQuery({ queryKey: ['ws-members', slug], queryFn: () => listWorkspaceMembers(slug!), enabled: !!slug, staleTime: 60_000, retry: false });
  const programCard = usePrograms(slug).data?.programs.find((p) => p.program === program) ?? null;

  // R2-H2: `?set=param` (from Today / Setup / a readiness row) opens the statement editor on the value.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !data?.control || params.get('set') !== 'param') return;
    opened.current = true;
    setStatementOpen(true);
    setEditing({ focusParam: true, opener: null, objectiveId: null });
  }, [data, params]);

  // R2-H9: the sticky footer never hides the focused control — the page scrolls with that much padding.
  // Punch list P2: below the tab-bar breakpoint the footer sits above the fixed mobile tab bar, so the
  // padding adds the tab bar's height rather than replacing index.css's tab-bar padding.
  useEffect(() => {
    const root = document.documentElement;
    const before = root.style.scrollPaddingBottom;
    const tabbar = () => {
      const el = document.querySelector<HTMLElement>('[data-mobile-tabbar]');
      return el && getComputedStyle(el).display !== 'none' ? el.offsetHeight : 0;
    };
    const apply = () => { root.style.scrollPaddingBottom = `${(footer.current?.offsetHeight ?? 0) + tabbar() + 16}px`; };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro && footer.current) ro.observe(footer.current);
    window.addEventListener('resize', apply);
    return () => { ro?.disconnect(); window.removeEventListener('resize', apply); root.style.scrollPaddingBottom = before; };
  }, [data]);

  // R2-H4: after a save, focus stays in the panel that asked for it — on the confirmation.
  useEffect(() => { if (saved) savedRef.current?.focus(); }, [saved]);

  if (isLoading) return <Spinner />;
  if (error || !data?.control) return <EmptyState title="Control not found" description="This control does not exist or you do not have access." />;
  const c = data.control;
  const periodId = c.readiness?.period_id ?? null;
  const objectives = c.readiness?.objectives ?? [];
  const couldntObjectives = objectives.filter((o) => o.couldnt_check);
  const firstGap = objectives.find(isGap);
  const firstOpen = objectives.find((o) => o.couldnt_check || isGap(o));
  const ownerId = c.assignments.find((a) => a.responsibility === 'owner')?.user_id ?? null;
  const nameOf = (id: string) => members.data?.members.find((m) => m.user_id === id)?.display_name ?? null;
  const ownerName = ownerId ? nameOf(ownerId) : null;
  const statement = c.current_revision?.statement ?? c.requirement.statement;
  const overall = !c.readiness ? null : c.readiness.couldnt_check ? 'couldnt' : c.readiness.status;
  const paramValues = c.current_revision?.org_param_values ?? {};
  // V109-7-3: a baseline-fixed value counts as set (the route says where each value comes from).
  const allParamsSet = c.parameters
    ? c.parameters.parameters.length > 0 && !c.parameters.odp_unset.length
    : (c.requirement.params ?? []).length > 0 && (c.requirement.params ?? []).every((p) => valueText(paramValues[p.id]).trim());
  const unsetParams = (c.parameters?.parameters ?? []).filter((p) => p.source === 'unset');
  const accepted = coveringAcceptance(c.readiness?.risk_acceptances);
  const ctx: FixContext = { program, requirement_id: c.requirement_id, period_id: periodId, ref: c.ref };
  const stage = currentStage(programCard);

  const fixesOf = (o: ControlObjective) => distinctFixes(prefix, objectiveItems(o.couldnt_check, o.objective_id), ctx);
  const openEditor = (opener: HTMLElement | null, focusParam: boolean, objectiveId: string | null) => {
    setSaved(null);
    setStatementOpen(true);
    setEditing({ focusParam, opener, objectiveId });
  };
  /** The fix for an objective: a value opens THIS page's statement editor (H9); evidence opens the upload here (R2-H3). */
  const runFix = (o: ControlObjective, f: FixLink | undefined, opener: HTMLElement | null) => {
    if (!f) return;
    if (f.destination === 'statement') { openEditor(opener, true, o.objective_id); return; }
    if (f.destination === 'attach' && periodId) { setUploadFor(o.objective_id); return; }
    navigate(f.href);
  };
  const fixLabelOf = (f: FixLink | undefined) => (f?.destination === 'statement' ? 'Set the value' : f?.label ?? 'Fix it');
  const evidenceHref = (o: ControlObjective) => `${prefix}/evidence?${new URLSearchParams({ objective: o.objective_id, ...(periodId ? { program: program!, period: periodId } : {}), for: `${c.ref} ${o.ref}` }).toString()}#attach`;
  const findingsHref = `${prefix}/findings?${new URLSearchParams({ requirement: c.ref, program: program!, ...(periodId ? { period: periodId } : {}) }).toString()}`;
  const acceptTargets = accepted || !couldntObjectives.length ? [] : [{
    label: `${c.ref} ${c.title}`, registerItemId: c.requirement_id,
    connection: (() => { const k = [...new Set(couldntObjectives.flatMap((o) => objectiveItems(o.couldnt_check).map((i) => i.kind)).filter((x): x is string => !!x))]; return k.length === 1 ? sourceNameOf(k[0]!) : null; })(),
  }];

  return (
    <div className="cd-page">
      <nav aria-label="Breadcrumb" className="cd-crumbs">
        <Link to={`${prefix}/programs/${program}`}>{programTitle(program!)}</Link>
        <span aria-hidden="true">/</span>
        {/* V109-5 built-UI (Medium "Control detail"): the program's CURRENT stage (Operate once certified). */}
        <Link to={`${prefix}/programs/${program}/${stage}`}>{stage === 'operate' ? 'Operate' : 'Prepare'}</Link>
        <span aria-hidden="true">/</span>
        <span>{c.ref} {c.title}</span>
      </nav>

      <h1 className="cd-h1"><span className="cd-ref">{c.ref}</span> · {c.title}</h1>
      <div className="cd-status" data-part="control-status">
        {overall === 'couldnt' ? <CouldntCheckChip /> : overall ? <Chip tone={OBJ_CHIP[overall].tone} text={OBJ_CHIP[overall].text} /> : null}
      </div>

      {/* R3 (Low): no dead end when nobody owns it — assign one in Team & assignments. */}
      <p className="cd-owner" data-part="control-owner">
        {ownerName ? `Assigned to ${ownerName}` : ownerId ? 'Assigned' : <>No owner yet · <Link to={`${prefix}/team`}>Assign one in Team & assignments</Link></>}
      </p>

      <details open={statementOpen} onToggle={(e) => setStatementOpen((e.target as HTMLDetailsElement).open)} id="control-statement" className="cd-card cd-statement">
        <summary><h2 className="cd-h2">Control statement</h2></summary>
        {editing ? (
          <StatementEditor slug={slug!} program={program!} requirementId={requirementId!} control={c} focusParam={editing.focusParam}
            onClose={() => { const back = editing.opener; setEditing(null); window.setTimeout(() => (back ?? document.querySelector<HTMLElement>('#control-statement summary'))?.focus(), 0); }}
            onSaved={(values) => {
              const back = editing;
              setEditing(null);
              const next = nextOfObjective(objectives.find((o) => o.objective_id === back.objectiveId) ?? couldntObjectives[0] ?? null);
              const shown = values.length ? values.join(', ') : 'none';
              setSaved({ objectiveId: back.objectiveId, text: `Value saved: ${shown}. GAC Pro uses it on the next check (${nextCollectionText(next)}).` });
            }} />
        ) : (
          <>
            <p className="cd-statement-text">{statement ?? 'No statement yet.'}</p>
            {saved && !saved.objectiveId ? <p className="cd-saved" role="status" tabIndex={-1} ref={savedRef}>{saved.text}</p> : null}
          </>
        )}
      </details>

      {/* V109-7-3 (PD-1): a value the organization must choose and hasn't is visible here whether or not a check ran — with its fix. */}
      {!editing && unsetParams.length ? (
        <div className="cc-panel" role="group" aria-labelledby="ctl-odp-unset-h" data-part="odp-unset">
          <p className="cc-panel-h" id="ctl-odp-unset-h">
            {unsetParams.length === 1 ? 'A value your organization must choose isn’t set' : `${unsetParams.length} values your organization must choose aren’t set`}
          </p>
          <p className="cc-fixer">
            {unsetParams.map((p) => p.label).join(' · ')}
            {unsetParams.some((p) => p.read_by.length) ? ' — GAC Pro can’t check this control until they’re set' : ''}
          </p>
          <div className="cc-panel-acts">
            <Button variant="secondary" onClick={(e) => openEditor(e.currentTarget, true, null)}>Set the value</Button>
          </div>
        </div>
      ) : null}

      <section className="cd-card" aria-labelledby="ctl-obj-h">
        <div className="cd-section-head">
          <h2 id="ctl-obj-h" className="cd-h2">Assessment objectives</h2>
          <div className="cd-counts" data-part="objective-counts">
            {(['met', 'not_met', 'no_evidence', 'undetermined'] as const).map((s) => {
              const count = objectives.filter((o) => o.status === s && !o.couldnt_check).length;
              return count ? <Chip key={s} tone={OBJ_CHIP[s].tone} text={`${count} ${OBJ_CHIP[s].text}`} /> : null;
            })}
            {couldntObjectives.length ? <CouldntCheckChip count={couldntObjectives.length} /> : null}
          </div>
        </div>
        {/* R2-H5: an open acceptance is shown (still Couldn't check); otherwise ONE way to accept it for the page. */}
        {couldntObjectives.length && accepted ? <AcceptedLine acceptance={accepted} ownerName={nameOf(accepted.owner_user_id)} /> : null}
        {couldntObjectives.length && admin ? <AcceptRisk slug={slug!} viewerId={viewerId} targets={acceptTargets}
          onDone={() => void qc.invalidateQueries({ queryKey: ['control-detail', slug, program, requirementId] })} /> : null}

        {!c.readiness ? (
          <EmptyState title="No assessment period yet" description="Objectives are judged once the program has a period."
            action={<Button variant="secondary" onClick={() => navigate(`${prefix}/programs/${program}/prepare`)}>Open Prepare</Button>} />
        ) : !objectives.length ? (
          <p className="cd-muted">This control has no assessment objectives yet.</p>
        ) : (
          <ol role="list" className="cd-objectives">
            {objectives.map((obj) => (
              <ObjectiveRow key={obj.objective_id} obj={obj} fixes={fixesOf(obj)} ctx={{ slug: slug!, program: program!, periodId, ref: c.ref }}
                waitingForCheck={allParamsSet && !!obj.couldnt_check?.reasons.includes('odp_unset')}
                saved={saved && saved.objectiveId === obj.objective_id ? saved.text : null} savedRef={savedRef}
                uploadOpen={uploadFor === obj.objective_id} onUploadClosed={() => setUploadFor(null)}
                evidenceHref={evidenceHref(obj)} findingsHref={findingsHref}
                onFix={(f, el) => runFix(obj, f, el)} fixLabel={fixLabelOf}
                onCollect={() => navigate(evidenceHref(obj))} onFindings={() => navigate(findingsHref)} />
            ))}
          </ol>
        )}
      </section>

      {/* Sticky actions: the first open objective's action leads — a gap or a Couldn't check (H2). */}
      <div className="cd-actions" data-part="control-actions" ref={footer}>
        {firstOpen?.couldnt_check ? (
          <Button variant="primary" onClick={(e) => runFix(firstOpen, fixesOf(firstOpen)[0], e.currentTarget)}>{`${fixLabelOf(fixesOf(firstOpen)[0])} · ${firstOpen.ref}`}</Button>
        ) : firstGap ? (
          <Button variant="primary" onClick={() => navigate(evidenceHref(firstGap))}>{`Collect for ${firstGap.ref}`}</Button>
        ) : null}
        {firstOpen?.couldnt_check && firstGap ? (
          <Button variant="secondary" onClick={() => navigate(evidenceHref(firstGap))}>{`Collect for ${firstGap.ref}`}</Button>
        ) : !firstOpen?.couldnt_check && couldntObjectives[0] ? (
          <Button variant="secondary" onClick={(e) => runFix(couldntObjectives[0]!, fixesOf(couldntObjectives[0]!)[0], e.currentTarget)}>{`${fixLabelOf(fixesOf(couldntObjectives[0]!)[0])} · ${couldntObjectives[0]!.ref}`}</Button>
        ) : null}
        <Button variant="ghost" onClick={(e) => openEditor(e.currentTarget, false, null)}>Edit statement</Button>
      </div>
    </div>
  );
}

/** The soonest next collection behind an objective's Couldn't check (null when none is scheduled). */
function nextOfObjective(o: ControlObjective | null): string | null {
  const t = (o?.couldnt_check?.by_source ?? []).map((s) => s.next_collection_at).filter((x): x is string => !!x).sort();
  return t[0] ?? null;
}

/** One objective: its status, and — when GAC Pro couldn't check it — the neutral panel with its fix (H2). */
function ObjectiveRow({ obj, fixes, ctx, waitingForCheck, saved, savedRef, uploadOpen, onUploadClosed, evidenceHref, findingsHref, onFix, fixLabel, onCollect, onFindings }: {
  obj: ControlObjective; fixes: FixLink[]; ctx: { slug: string; program: string; periodId: string | null; ref: string };
  waitingForCheck: boolean; saved: string | null; savedRef: React.RefObject<HTMLParagraphElement>;
  uploadOpen: boolean; onUploadClosed: () => void; evidenceHref: string; findingsHref: string;
  onFix: (f: FixLink, opener: HTMLElement) => void; fixLabel: (f: FixLink | undefined) => string; onCollect: () => void; onFindings: () => void;
}) {
  const [all, setAll] = useState(false);
  const oc = OBJ_CHIP[obj.status];
  const items = objectiveItems(obj.couldnt_check, obj.objective_id);
  const panelH = `cc-panel-h-${obj.objective_id.replace(/[^A-Za-z0-9-]/g, '-')}`;
  const auto = fixes.some((f) => f.view.fixer === 'gac_pro' && f.view.customer_step);
  const next = nextOfObjective(obj);
  const attach = fixes.find((f) => f.destination === 'attach');
  return (
    <li id={anchor(obj.ref)} className="cd-objective">
      <div className="cd-obj-head">
        <span className="cd-obj-ref">{obj.ref}</span>
        {obj.couldnt_check ? <CouldntCheckChip /> : <Chip tone={oc.tone} text={oc.text} />}
        <span className="cd-obj-text">{obj.statement}</span>
        {/* R3 (Low): a quiet way to this objective's evidence. */}
        <Link className="cd-quiet" to={evidenceHref} aria-label={`Evidence for ${obj.ref}`}>Evidence</Link>
      </div>

      {obj.couldnt_check ? (
        // H2: the same weight as a gap — a neutral panel (a labelled group), the reasons with what they leave
        // unverified, who can fix it, and the fix as a DS secondary button.
        <div className="cc-panel cd-obj-body" role="group" aria-labelledby={panelH} data-part="couldnt-check">
          <p className="cc-panel-h" id={panelH}>
            {waitingForCheck ? 'Waiting for the next check' : `Not verified · ${obj.couldnt_check.unverified} of ${obj.couldnt_check.resources} resource${obj.couldnt_check.resources === 1 ? '' : 's'}`}
          </p>
          <CouldntCheckWhy items={items} unit="resource" expanded={all} />
          <ShowAllReasons items={items} expanded={all} onToggle={() => setAll((x) => !x)} />
          <p className="cc-fixer">{[...new Set(fixes.map((f) => f.view.fixer_words))].join(' · ')}</p>
          {/* R3 (Low): when GAC Pro collects next; "shortly" once that time has passed. */}
          {auto ? <p className="cc-next" data-part="next-collection">{`GAC Pro collects again ${nextCollectionText(next)}`}</p> : null}
          {saved ? <p className="cd-saved" role="status" tabIndex={-1} ref={savedRef}>{saved}</p> : null}
          <div className="cc-panel-acts">
            {(all ? fixes : fixes.slice(0, 2)).map((f) => (
              <Button key={f.href} variant="secondary" data-part="couldnt-check-fix" data-href={f.href} onClick={(e) => onFix(f, e.currentTarget)}>
                {waitingForCheck && f.destination === 'statement' ? 'Change the value' : fixLabel(f)}
              </Button>
            ))}
          </div>
          {attach && ctx.periodId && uploadOpen ? (
            <UploadForObjective slug={ctx.slug} program={ctx.program} periodId={ctx.periodId} refLabel={obj.ref} objectives={[{ id: obj.objective_id, ref: obj.ref }]}
              testKey={items.find((i) => i.test_key)?.test_key ?? null} startOpen onUploaded={onUploadClosed} label={`Attach evidence for ${obj.ref}`} />
          ) : null}
        </div>
      ) : isGap(obj) ? (
        <div className="cd-gap cd-obj-body">
          <p className="cd-gap-text">
            {obj.status === 'not_met' ? 'Not met: the evidence shows a gap' : obj.status === 'undetermined' ? 'Not judged yet for this period' : 'No evidence collected for this objective'}
          </p>
          {/* R3 (Medium): a gap's findings open on Findings, for this control; collecting opens the objective's upload. */}
          {obj.status === 'not_met'
            ? <Button variant="secondary" data-href={findingsHref} onClick={onFindings}>Open findings</Button>
            : <Button variant="secondary" onClick={onCollect}>Collect evidence</Button>}
        </div>
      ) : null}
    </li>
  );
}

/**
 * H9: the statement and the organization's parameter values, edited on this page. `focusParam`
 * lands on the first value still unset (the "Couldn't check · a value your organization must
 * choose" fix). POST …/revisions; the route decides who may write (403 is shown, not hidden).
 * R2-H8: Cancel and Escape return focus to whatever opened it; a save moves focus to the confirmation.
 */
function StatementEditor({ slug, program, requirementId, control, focusParam, onClose, onSaved }: {
  slug: string; program: string; requirementId: string; control: ProgramControlResponse['control']; focusParam: boolean;
  onClose: () => void; onSaved: (values: string[]) => void;
}) {
  const qc = useQueryClient();
  const params = control.requirement.params ?? [];
  const current = control.current_revision?.org_param_values ?? {};
  const [statement, setStatement] = useState(control.current_revision?.statement ?? control.requirement.statement ?? '');
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(params.map((p) => [p.id, valueText(current[p.id])])));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const unset = params.find((p) => !values[p.id]?.trim());
    const el = document.getElementById(focusParam && (unset ?? params[0]) ? `ctl-param-${(unset ?? params[0])!.id}` : 'ctl-statement');
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    if (!statement.trim()) { setError('Write the statement first.'); return; }
    setBusy(true); setError(null);
    try {
      const org = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]));
      await req(`/api/w/${slug}/programs/${encodeURIComponent(program)}/controls/${encodeURIComponent(requirementId)}/revisions`, {
        method: 'POST', body: JSON.stringify({ statement: statement.trim(), org_param_values: org, base_revision_id: control.current_revision?.revision_id ?? null }),
      });
      await qc.invalidateQueries({ queryKey: ['control-detail', slug, program, requirementId] });
      onSaved(Object.values(org));
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'Only an owner, an admin, the program lead or this control\'s owner can change it.' : err instanceof Error ? err.message : 'That did not save. Try again.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} noValidate className="cd-editor" aria-label="Edit the control statement" data-part="statement-editor"
      onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); onClose(); } }}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="ctl-statement" label="Statement" required>
        <Textarea rows={4} value={statement} onChange={(e) => setStatement(e.target.value)} />
      </Field>
      {params.length ? (
        <fieldset id="ctl-parameters" className="cd-params">
          <legend>Your organization's values</legend>
          {params.map((p) => (
            <Field key={p.id} id={`ctl-param-${p.id}`} label={p.label || p.id}>
              <Input value={values[p.id] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [p.id]: e.target.value }))} />
            </Field>
          ))}
        </fieldset>
      ) : null}
      <div className="cd-acts">
        <Button type="submit" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save'}</Button>
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
